import {
  Score,
  Stem,
  StemGraphicGapMatch,
  StemTextInline,
} from "@examspark/types";

import { scoreValue } from "./math/symbolic";

import { getNewScoreNullable } from ".";

export const scoreTextInline = (
  expected: StemTextInline,
  actual: Stem.Response,
): Score => {
  const score = getNewScoreNullable();
  const { scoring, content } = expected;

  let gapIds: string[] = [];
  if (scoring?.gaps) {
    gapIds = Object.keys(scoring.gaps);
  }

  score.max = gapIds.length;

  // if there are any extended-entry gaps, we cannot auto-score
  // this question
  if (Object.values(content.gaps)?.some((g) => g.type === "extended-entry")) {
    score.isScorable = false;
    score.actual = undefined;
    return score;
  } else {
    score.actual = 0;
  }

  if (!actual?.gaps) {
    return score;
  }

  // get the gap type
  for (const k in actual.gaps) {
    const gap = content.gaps[k];

    if (scoreSingleGap(gap, k, actual.gaps, expected, scoring?.gaps)) {
      score.actual++;
    }
  }

  return score;
};

export const scoreTextEntryGap = (
  gapId: string,
  expected: StemTextInline | StemGraphicGapMatch,
  actualValue: string,
  scoring: Stem.ScoringGaps,
) => {
  if (!expected.scoring?.gaps?.[gapId]) return;
  if (!expected.content?.gaps?.[gapId]) return;

  /** make inline text-entry scoring more forgiving */
  const normalizeLoose = (text: string | undefined, matchCase: boolean) => {
    let normalized = (text ?? "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .replace(/\s+/g, "");

    if (!matchCase) {
      normalized = normalized.toLowerCase();
    }

    return normalized;
  };

  const normalizeStrict = (text: string | undefined, matchCase: boolean) => {
    let normalized = text?.trim() ?? "";

    if (!matchCase) {
      normalized = normalized.toLowerCase();
    }

    return normalized;
  };

  for (const gap of expected.content.gaps[gapId].options) {
    const value = gap.body;
    const optionId = gap.id;

    const scoringSettings = {
      matchCase: expected.content.gaps[gapId].matchCase ?? false,
      matchStrategy: expected.content.gaps[gapId].matchStrategy ?? "contains",
    };

    if (scoringSettings.matchStrategy === "contains") {
      const normalizedActual = normalizeLoose(
        actualValue,
        scoringSettings.matchCase,
      );
      const normalizedExpected = normalizeLoose(
        value,
        scoringSettings.matchCase,
      );

      if (!normalizedActual.match(new RegExp(normalizedExpected))) continue;
    } else if (scoringSettings.matchStrategy === "exact") {
      const normalizedActual = normalizeStrict(
        actualValue,
        scoringSettings.matchCase,
      );
      const normalizedExpected = normalizeStrict(
        value,
        scoringSettings.matchCase,
      );

      if (normalizedActual !== normalizedExpected) continue;
    }

    if (scoring?.[gapId].options[optionId]?.isKey) {
      return true;
    }
  }
  return false;
};

export const scoreNumericEntryGap = (
  gapId: string,
  expected: StemTextInline | StemGraphicGapMatch,
  actualValue: string,
  scoring: Stem.ScoringGaps,
) => {
  if (!expected.scoring?.gaps?.[gapId]) return;
  if (!expected.content?.gaps?.[gapId]) return;

  /** make inline text-entry scoring more forgiving */
  const normalizeText = (text: string) => {
    return text.toLowerCase().replace(/[\s,]+/g, "");
  };

  const precision = !isNaN(Number(expected.content.gaps[gapId].precision))
    ? Number(expected.content.gaps[gapId].precision)
    : null;

  for (const gap of expected.content.gaps[gapId].options) {
    const value = gap.body;
    const optionId = gap.id;
    const normalizedActual = normalizeText(actualValue ?? "");
    const normalizedExpected = normalizeText(value ?? "");

    if (Number(normalizedActual) !== Number(normalizedExpected)) continue;
    // also check precision if it exists
    if (precision && normalizedActual.split(".")?.[1]?.length !== precision)
      continue;
    if (scoring?.[gapId].options[optionId]?.isKey) {
      return true;
    }
  }
  return false;
};

export const scoreMathEntryGap = (
  gapId: string,
  expected: StemTextInline | StemGraphicGapMatch,
  actualValue: string,
  scoring: Stem.ScoringGaps,
) => {
  if (!scoring?.[gapId]) return;
  if (!expected.content?.gaps?.[gapId]) return;

  for (const gap of expected.content.gaps[gapId].options) {
    const value = gap.body;
    const optionId = gap.id;

    if (value == null) continue;

    if (!scoreValue(actualValue, value)) continue;

    if (scoring?.[gapId].options[optionId]?.isKey) {
      return true;
    }
  }
  return false;
};

export const scoreExtendedEntryGap = (
  gapId: string,
  expected: StemTextInline | StemGraphicGapMatch,
  actualValue: string,
  scoring: Stem.ScoringGaps,
) => {
  return false;
};

export const scoreSingleGap = (
  gap: Stem.Gap,
  id: Stem.ResponseGaps[number],
  actual: Stem.Response["gaps"],
  expected: StemTextInline | StemGraphicGapMatch,
  scoring: Stem.ScoringGaps | undefined,
) => {
  switch (gap.type) {
    case "inline-choice":
    case "inline-choice-richtext":
      return scoring?.[id]?.options?.[actual?.[id] as any]?.isKey;
    case "text-entry":
      return (
        scoring?.[id] && scoreTextEntryGap(id, expected, actual?.[id], scoring)
      );
    case "numeric-entry":
      return (
        scoring?.[id] &&
        scoreNumericEntryGap(id, expected, actual?.[id], scoring)
      );
    case "math-entry":
      return (
        scoring?.[id] && scoreMathEntryGap(id, expected, actual?.[id], scoring)
      );
    default:
      return false;
  }
};
